import { Controller } from "@hotwired/stimulus"

// Handles document upload auto-submission and loading states
// Automatically submits the upload form when a file is selected and manages visual feedback
//
// Usage:
//   On the form element:
//     data-controller="tax-organizer--document-upload"
//     data-tax-organizer--document-upload-target="form"
//
//   On the file input:
//     data-tax-organizer--document-upload-target="fileInput"
//     data-action="change->tax-organizer--document-upload#handleFileChange"
//
//   Optional loading indicator:
//     data-tax-organizer--document-upload-target="loadingIndicator"
//
export default class extends Controller {
  static targets = ["form", "fileInput", "loadingIndicator"]
  static values = {
    minDisplayTime: { type: Number, default: 150 }
  }

  connect() {
    this.startTime = null
  }

  handleFileChange(event) {
    const fileInput = event.target

    if (fileInput.files.length > 0) {
      this.showLoading()
      this.addUploadingClasses()

      // Submit the form
      const form = fileInput.closest('form')
      if (form) {
        form.requestSubmit()
      }
    }
  }

  // Called when Turbo starts submitting the form
  handleSubmitStart() {
    this.showLoading()
    this.addUploadingClasses()
    this.setDataSaving(true)
  }

  // Called when Turbo finishes submitting the form
  handleSubmitEnd() {
    // Note: If upload succeeds, the turbo frame will be replaced with new content
    // This handles the error case where the form stays in the DOM
    this.hideLoading()
    this.removeUploadingClasses()
    this.setDataSaving(false)
  }

  showLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.startTime = Date.now()
      this.loadingIndicatorTarget.classList.remove('d-none')
    }
  }

  hideLoading() {
    if (this.hasLoadingIndicatorTarget && this.startTime) {
      const elapsedTime = Date.now() - this.startTime
      const remainingTime = Math.max(0, this.minDisplayTimeValue - elapsedTime)

      setTimeout(() => {
        this.loadingIndicatorTarget.classList.add('d-none')
        this.startTime = null
      }, remainingTime)
    }
  }

  addUploadingClasses() {
    if (this.hasFormTarget) {
      this.formTarget.classList.add('uploading')
    }
    if (this.hasFileInputTarget) {
      this.fileInputTarget.classList.add('uploading')
    }
  }

  removeUploadingClasses() {
    if (this.hasFormTarget) {
      this.formTarget.classList.remove('uploading')
    }
    if (this.hasFileInputTarget) {
      this.fileInputTarget.classList.remove('uploading')
    }
  }

  setDataSaving(isSaving) {
    // Find the parent document card and set data-saving attribute
    const documentCard = this.element.closest('.document-item')
    if (documentCard) {
      if (isSaving) {
        documentCard.setAttribute('data-saving', 'true')
      } else {
        documentCard.removeAttribute('data-saving')
      }
    }
  }
}
